import PropTypes from 'prop-types';
import { gql, NetworkStatus, useLazyQuery, useQuery } from '@common/hooks';
import React, { useEffect, useState } from 'react';
import { Typography } from 'antd';
import { CandidateLink } from 'works/legacy/components/candidate-link';
import Filters from 'works/legacy/lib/filters';
import {
  buildOptionsFromData,
  ErrorHandler,
  FilteredTable,
  filtersForPaginatedAttr,
  MultiSelectTableFilter,
  DeprecatedStatePill,
  TextOverflow,
  TextSearchTableFilter,
  TitleAndActions,
} from '@common/components';
import { FontSizes } from 'works/legacy/lib/style-helper';
import { OnDemandTeamPageActions } from '@scheduling/components/on-demand-team-page-actions';
import { RolesQuery } from '@clinician-profile/graphql';
import { useQueryParams } from 'works/legacy/services/query-params-service';
import { WORKFORCE_GROUP_FLAT_QUERY } from '@scheduling/graphql';

const { Text } = Typography;

const PaginatedWorkersQuery = gql`
  query PaginatedWorkersQuery(
    $limit: Int!
    $offset: Int!
    $orderDirection: OrderDirection
    $orderField: WorkerOrderField
    $filters: WorkerFilterInput
  ) {
    paginatedWorkers(
      limit: $limit
      offset: $offset
      orderDirection: $orderDirection
      orderField: $orderField
      filters: $filters
    ) {
      workers {
        id
        active
        employeeId
        worksUser {
          id
          firstName
          lastName
          email
          timeZoneAbbreviation
        }
        homeUnit {
          id
          name
          costCenter
        }
        allowedUnits {
          id
          name
        }
        role {
          id
          abbreviation
        }
        workforceGroup {
          id
          name
        }
      }
      totalCount
    }
  }
`;

const WORKS_ORGANIZATION_UNIT_QUERY = gql`
  query WorksOrganizationUnits {
    organizationUnits {
      id
      name
    }
  }
`;

export function OnDemandTeamTable({
  membershipError,
  showHeader = false,
  canCreateWorkers,
  canUploadWorkers,
}) {
  const sortingAttributeDict = {
    name: 'NAME',
    homeUnit: 'HOME_UNIT',
    workforceGroup: 'WORKFORCE_GROUP',
  };

  const LOCALSTORAGE_FILTER_KEY = 'OnDemandWorkerTable';
  const queryParams = useQueryParams();
  const [filters, setFilters] = useState(() => {
    const userFilters = Filters.getFilters(LOCALSTORAGE_FILTER_KEY);
    const queryParamFilters = {
      active: [queryParams.get('active')],
      workforceGroup: [queryParams.get('workforceGroup')],
    };

    const normalize = (obj) => {
      return Object.fromEntries(
        Object.entries(obj)
          .map(([key, value]) => {
            if (Array.isArray(value)) {
              const filtered = value.filter((v) => v !== null);
              return [key, filtered.length > 0 ? filtered : null];
            }
            return [key, value];
          })
          .filter(([_, value]) => value !== null)
      );
    };

    return {
      active: ['true'],
      ...normalize(userFilters),
      ...normalize(queryParamFilters),
    };
  });

  const PAGE_SIZE = 10;
  const [paginationAttrs, setPaginationAttrs] = useState({
    limit: PAGE_SIZE,
    offset: 0,
  });

  const {
    loading: rolesLoading,
    error: rolesError,
    data: rolesData,
  } = useQuery(RolesQuery);

  const [workersQuery, { error, data, loading, networkStatus }] = useLazyQuery(
    PaginatedWorkersQuery,
    {
      notifyOnNetworkStatusChange: true,
    }
  );

  useEffect(() => {
    workersQuery({
      variables: {
        ...paginationAttrs,
        filters,
      },
    });
  }, [filters, paginationAttrs, workersQuery]);

  const {
    loading: organizationUnitLoading,
    data: organizationUnitData,
    error: organizationUnitError,
  } = useQuery(WORKS_ORGANIZATION_UNIT_QUERY, {
    variables: {
      input: {
        scope: 'organization',
      },
    },
  });

  const {
    loading: workforceGroupLoading,
    data: workforceGroupData,
    error: workforceGroupError,
  } = useQuery(WORKFORCE_GROUP_FLAT_QUERY, {
    fetchPolicy: 'cache-first',
  });

  if (
    error ||
    rolesError ||
    membershipError ||
    organizationUnitError ||
    workforceGroupError
  ) {
    return <ErrorHandler error={error} log="OnDemandTeamTable" />;
  }

  const isLoaded =
    networkStatus === NetworkStatus.ready &&
    data &&
    organizationUnitData &&
    !organizationUnitLoading &&
    !rolesLoading &&
    !workforceGroupLoading;
  const workers = isLoaded ? data.paginatedWorkers.workers : [];
  const total = isLoaded ? data.paginatedWorkers.totalCount : null;

  const columns = [
    {
      title: 'Name',
      dataIndex: 'worksUser',
      key: 'name',
      width: '20%',
      sorter: true,
      render: (worksUser, record) => {
        return (
          <CandidateLink
            firstName={worksUser.firstName}
            lastName={worksUser.lastName}
            link={`/works/team/on_demand/${record.id}`}
          />
        );
      },
    },
    {
      title: 'EEID',
      dataIndex: 'employeeId',
      key: 'employeeId',
      width: '10%',
      filtered: filters.employeeId || false,
      filteredValue: filters.employeeId || null,
      filterDropdown: TextSearchTableFilter,
    },
    {
      title: 'Role',
      key: 'role',
      width: '7%',
      filtered: filters.role || false,
      filteredValue: filters.role || null,
      filters: filtersForPaginatedAttr(
        rolesData && rolesData.roles,
        (role) => role.abbreviation,
        (role) => ({
          text: role.abbreviation,
          value: role.id,
        })
      ),
      filterDropdown: MultiSelectTableFilter,
      render: (record) => {
        return <>{record.role.abbreviation}</>;
      },
    },
    {
      title: 'Group',
      dataIndex: 'workforceGroup',
      key: 'workforceGroup',
      width: '10%',
      filtered: filters.workforceGroup || false,
      filteredValue: filters.workforceGroup || null,
      filters: buildOptionsFromData(
        workforceGroupData && workforceGroupData.workforceGroups,
        (workforceGroup) => ({
          text: workforceGroup.name,
          value: workforceGroup.id,
        })
      ),
      filterDropdown: MultiSelectTableFilter,
      sorter: true,
      render: (record) => {
        return <>{record?.name || 'None'}</>;
      },
    },
    {
      title: 'Home Unit',
      dataIndex: 'homeUnit',
      key: 'homeUnit',
      width: '20%',
      filtered: filters.homeUnit || false,
      filteredValue: filters.homeUnit || null,
      filters: buildOptionsFromData(
        organizationUnitData && organizationUnitData.organizationUnits,
        (organizationUnit) => ({
          text: organizationUnit.name,
          value: organizationUnit.id,
        })
      ),
      filterDropdown: MultiSelectTableFilter,
      sorter: true,
      render: (record) => {
        if (!record) {
          return <>None</>;
        }
        return (
          <>
            <Text>{record.name}</Text>
            <br />
            <Text type="secondary">{record.costCenter}</Text>
          </>
        );
      },
    },
    {
      title: 'Approved Units',
      dataIndex: 'allowList',
      key: 'allowList',
      width: '27%',
      filtered: filters.allowList || false,
      filteredValue: filters.allowList || null,
      filters: filtersForPaginatedAttr(
        organizationUnitData && organizationUnitData.organizationUnits,
        (organizationUnit) => organizationUnit.name,
        (organizationUnit) => ({
          text: organizationUnit.name,
          value: organizationUnit.id,
        })
      ),
      filterDropdown: MultiSelectTableFilter,
      render: (_, record) => {
        let allowedUnitsText = record.allowedUnits.reduce((prev, unit) => {
          const isHomeUnit = record?.homeUnit?.name === unit.name;
          return `${prev}, ${unit.name}${isHomeUnit ? ' (Home Unit)' : ''}`;
        }, '');

        allowedUnitsText = allowedUnitsText.substring(2);
        return <TextOverflow>{allowedUnitsText}</TextOverflow>;
      },
    },
    {
      title: 'Status',
      dataIndex: 'active',
      key: 'active',
      filtered: filters.active || false,
      filteredValue: filters.active || null,
      filters: [
        {
          text: 'Active',
          value: 'true',
        },
        {
          text: 'Inactive',
          value: 'false',
        },
      ],
      render: (active) => {
        return (
          <DeprecatedStatePill
            color={active ? 'grass' : 'coral'}
            fontSize={FontSizes.small}
          >
            {active ? 'Active' : 'Inactive'}
          </DeprecatedStatePill>
        );
      },
    },
  ];

  const onFilterChange = (newFilters) => {
    setFilters(newFilters);
    Filters.setFilters(LOCALSTORAGE_FILTER_KEY, newFilters);
  };

  const handleTableChange = (pagination, newFilters, sorter) => {
    const pageSize = pagination.pageSize || PAGE_SIZE;
    const newPaginationAttrs = {
      offset: (pagination.current - 1) * pageSize,
      limit: pageSize,
      orderField: sortingAttributeDict[sorter?.column?.key],
    };

    if (sorter?.order) {
      newPaginationAttrs.orderDirection =
        sorter.order === 'ascend' ? 'ASC' : 'DESC';
    }
    setPaginationAttrs(newPaginationAttrs);
    onFilterChange(newFilters);
  };

  return (
    <>
      {showHeader && (
        <TitleAndActions
          title="Team"
          actions={
            <>
              <OnDemandTeamPageActions
                canCreateWorkers={canCreateWorkers}
                canUploadWorkers={canUploadWorkers}
              />
            </>
          }
        />
      )}
      <FilteredTable
        columns={columns}
        tableLayout="fixed"
        rowKey="id"
        dataSource={workers}
        loading={loading}
        pagination={{ ...paginationAttrs, total }}
        onChange={handleTableChange}
        onFilterChange={onFilterChange}
        localStorageFilterKey={LOCALSTORAGE_FILTER_KEY}
        filters={filters}
      />
    </>
  );
}

OnDemandTeamTable.propTypes = {
  membershipError: PropTypes.bool,
  showHeader: PropTypes.bool,
  canCreateWorkers: PropTypes.bool,
  canUploadWorkers: PropTypes.bool,
};
